import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  limit,
} from '@firebase/firestore';
import { db } from './firebase';
import type { PlaceEvent, EventCreateInput } from '../types';

const COLLECTION_NAME = 'events';

/**
 * Create a new event
 */
export async function createEvent(event: EventCreateInput): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION_NAME), {
    ...event,
    createdAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Get all events for a place
 */
export async function getEventsForPlace(placeId: string): Promise<PlaceEvent[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceEvent[];
}

/**
 * Get an event by ID
 */
export async function getEventById(id: string): Promise<PlaceEvent | null> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PlaceEvent;
}

/**
 * Get upcoming/recent events across all places
 */
export async function getUpcomingEvents(
  maxResults = 10
): Promise<PlaceEvent[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceEvent[];
}

/**
 * Delete an event (only by creator)
 */
export async function deleteEvent(
  eventId: string,
  userId: string
): Promise<boolean> {
  const event = await getEventById(eventId);

  if (!event) {
    return false;
  }

  if (event.createdBy !== userId) {
    throw new Error('Nur der Ersteller kann dieses Event löschen');
  }

  await deleteDoc(doc(db, COLLECTION_NAME, eventId));
  return true;
}

/**
 * Subscribe to events for a place (real-time)
 */
export function subscribeToEvents(
  placeId: string,
  callback: (events: PlaceEvent[]) => void
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const events = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PlaceEvent[];
    callback(events);
  });
}

/**
 * Get events created by a user
 */
export async function getUserEvents(userId: string): Promise<PlaceEvent[]> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('createdBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceEvent[];
}
